/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureFlagContext } from '@eu-data-act/react-euda-commons/dist/index';
import { Button, Card, IconCopy, useTheme } from '@veneer/core';

import TelemetryDownloadButton from './telemetry-download-button';
import TelemetryRequestButton from './telemetry-request-button';

import {
  DeviceItemContainer,
  DeviceCardImageWrapper,
  NoImagePlaceholder,
  DeviceCardHeader,
  DeviceCardTextContainer,
  DeviceDetailCaption,
  DeviceDetailCaptionText,
  DeviceCardFooter,
  InlineNotificationContainer,
  StyledTextbox,
  StyledInlineNotification,
  InlineErrorCard
} from './styled';

import { OperationStatus, OperationResult } from '../../types';
import { Device, CategoryType as CategoryTypes } from '../../types/devices';
import {
  DeviceTelemetryStatus,
  PostDeviceTelemetryPayload,
  DeviceTelemetryRequest,
  DeviceTelemetryByStatus
} from '../../types/device-telemetry';
import { RootState } from '../../store';

import { handleCopyCode } from '../../utils/common';

import usePostTelemetryToast from '../../hooks/usePostTelemetryToast';
import { useDependencyManagerContext } from '../../contexts/dependencyManager';
import { useTabIndex } from '../../contexts/tab-index';

import {
  postDeviceTelemetryCleanup,
  postDeviceTelemetryRequest
} from '../../features/telemetry/device-telemetry-slice';

import PDFLearnMoreButton from '../pdf-learn-more-button';

type DeviceItemProps = {
  device: Device;
  'data-testid': string;
  CategoryType: string;
  deviceStartTabIndex?: number;
};

const DeviceItemContent: React.FC<DeviceItemProps> = (props) => {
  const {
    device,
    'data-testid': dataTestId,
    CategoryType,
    deviceStartTabIndex
  } = props;
  const theme = useTheme();
  const dispatch = useDispatch();
  const { getNextTabIndex } = useTabIndex();
  const { releasePlatformDataSharingTermsAndConditions } =
    useFeatureFlagContext();

  let buttonTabIndex = 0;
  const getDeviceButtonTabIndex = () => {
    if (deviceStartTabIndex !== undefined) {
      return deviceStartTabIndex + buttonTabIndex++;
    }
    return getNextTabIndex();
  };
  const [isDataRequested, setIsDataRequested] = useState(false);
  const [hasPendingTelemetry, setHasPendingTelemetry] = useState(false);

  const {
    translate,
    northboundAPIs: { v1 }
  } = useDependencyManagerContext();
  const { releasePlatformDeviceTelemetryInlineErrorShowReferenceCode } =
    useFeatureFlagContext();

  const {
    deviceTelemetry,
    postDeviceTelemetry,
    getDeviceTelemetry
  }: {
    deviceTelemetry: DeviceTelemetryByStatus;
    postDeviceTelemetry: OperationResult;
    getDeviceTelemetry: OperationResult;
  } = useSelector((state: RootState) => ({
    deviceTelemetry:
      state.DeviceTelemetryReducer?.deviceTelemetry?.[device.device_type]?.[
        device.device_serial_nr
      ] ?? {},
    postDeviceTelemetry: state.DeviceTelemetryReducer?.postDeviceTelemetry?.[
      device.device_serial_nr
    ] || {
      status: OperationStatus.NOT_STARTED
    },
    getDeviceTelemetry: state.DeviceTelemetryReducer?.getDeviceTelemetry || {
      status: OperationStatus.NOT_STARTED
    }
  }));

  const { hasCompletedTelemetry, queuedRequests, createdRequests } =
    useMemo(() => {
      const hasCompletedTelemetry =
        deviceTelemetry[DeviceTelemetryStatus.COMPLETED]?.length > 0;
      const queuedRequests = deviceTelemetry[DeviceTelemetryStatus.QUEUED];
      const createdRequests = deviceTelemetry[DeviceTelemetryStatus.CREATED];

      return {
        hasCompletedTelemetry,
        queuedRequests,
        createdRequests
      };
    }, [deviceTelemetry]);

  usePostTelemetryToast(postDeviceTelemetry, device);

  useEffect(() => {
    return () => {
      dispatch(postDeviceTelemetryCleanup());
    };
  }, [dispatch]);

  useEffect(() => {
    if (isDataRequested) {
      if (getDeviceTelemetry.status === OperationStatus.COMPLETED) {
        setIsDataRequested(false);
      }
      return;
    }
    if (postDeviceTelemetry.status === OperationStatus.COMPLETED) {
      setIsDataRequested(true);
    }
  }, [postDeviceTelemetry, getDeviceTelemetry]);

  // Update hasPendingTelemetry when deviceTelemetry changes
  useEffect(() => {
    if (createdRequests?.length > 0 || queuedRequests?.length > 0) {
      setHasPendingTelemetry(true);
    } else {
      setHasPendingTelemetry(false);
    }
  }, [deviceTelemetry]);

  const hasMostRecentTelemetryFailed = (): boolean => {
    const allRequests: DeviceTelemetryRequest[] = [];

    Object.values(deviceTelemetry).forEach((statusRequests) => {
      if (statusRequests) {
        allRequests.push(...statusRequests);
      }
    });

    if (allRequests.length === 0) {
      return false;
    }

    const mostRecentRequest = allRequests.reduce((latest, current) => {
      const latestTimestamp = latest.created_at;
      const currentTimestamp = current.created_at;

      return new Date(currentTimestamp) > new Date(latestTimestamp)
        ? current
        : latest;
    });

    return mostRecentRequest.status === DeviceTelemetryStatus.FAILED;
  };
  const getFailedRequestTraceId = (): string => {
    const failedRequests = deviceTelemetry[DeviceTelemetryStatus.FAILED] || [];
    return failedRequests.length > 0 ? failedRequests[0].trace_id || '' : '';
  };

  useEffect(() => {
    return () => {
      dispatch(postDeviceTelemetryCleanup());
    };
  }, []);

  const handleDownloadClick = (): void => {
    const completedRequests = deviceTelemetry[DeviceTelemetryStatus.COMPLETED];

    if (!completedRequests || completedRequests.length === 0) {
      return;
    }

    const requestIds = completedRequests.map(
      (request: DeviceTelemetryRequest) => ({
        id: request.id,
        dateRequested: request.created_at
      })
    );

    releasePlatformDataSharingTermsAndConditions
      ? v1.navigation.push('/terms-and-conditions', { requestIds })
      : v1.navigation.push('/download', { requestIds });
  };

  const handleDeviceTelemetry = (): void => {
    const telemetryPayload: PostDeviceTelemetryPayload = {
      device: {
        device_type: device.device_type,
        device_sub_type: device.device_sub_type,
        device_serial_nr: device.device_serial_nr,
        device_model: device.device_model,
        device_family: device.device_family,
        device_telemetry_format: device.device_telemetry_format
      }
    };

    dispatch(postDeviceTelemetryRequest(telemetryPayload));
  };

  const isPersonalOrPagewide =
    CategoryType === CategoryTypes.PERSONAL_SYSTEMS ||
    CategoryType === CategoryTypes.PAGEWIDE_WEB_PRESS;

  const shouldShowTelemetryButton = isPersonalOrPagewide
    ? hasPendingTelemetry
    : true;

  const isDisabled = hasPendingTelemetry || isDataRequested;

  return (
    <DeviceItemContainer
      data-testid={`${dataTestId}-device-details`}
      $veneerTheme={theme}
    >
      <DeviceCardImageWrapper>
        {device.device_image_url ? (
          <img
            src={device.device_image_url}
            alt={translate('components.device-item.image-alt', {
              deviceFamily:
                device.device_family ||
                translate('components.device-item.no-data.unknown'),
              serialNumber: device.device_serial_nr
            })}
          />
        ) : (
          <NoImagePlaceholder $veneerTheme={theme} />
        )}
      </DeviceCardImageWrapper>

      <DeviceCardHeader $veneerTheme={theme}>
        <DeviceCardTextContainer>
          <p className="body-regular">
            {device.device_family ||
              translate('components.device-item.no-data.name')}
          </p>
          <DeviceDetailCaption>
            <DeviceDetailCaptionText
              className="label-small"
              $veneerTheme={theme}
            >
              {device.device_model ||
                translate('components.device-item.no-data.model')}
            </DeviceDetailCaptionText>
            <DeviceDetailCaptionText
              className="label-small"
              $veneerTheme={theme}
            >
              {translate('components.device-item.serial-number', {
                serialNumber: device.device_serial_nr
              })}
            </DeviceDetailCaptionText>
          </DeviceDetailCaption>
        </DeviceCardTextContainer>

        <PDFLearnMoreButton
          categoryType={CategoryType}
          deviceTelemetryFormat={device.device_telemetry_format}
          tabIndex={getDeviceButtonTabIndex()}
          data-testid={`${dataTestId}-learn-more-button`}
        />
      </DeviceCardHeader>

      {hasMostRecentTelemetryFailed() && !isDataRequested && (
        <InlineErrorCard $veneerTheme={theme}>
          <StyledInlineNotification
            type="negative"
            closeButton={false}
            description={
              releasePlatformDeviceTelemetryInlineErrorShowReferenceCode ? (
                <InlineNotificationContainer
                  $veneerTheme={theme}
                  data-testid={`${dataTestId}-inline-notification`}
                >
                  <p style={{ textAlign: 'left' }}>
                    {translate(
                      'components.device-details-viewer.device-card.inline-notifications.errored-2'
                    )}
                  </p>
                  <StyledTextbox
                    data-testid={`${dataTestId}-trace-id-textbox`}
                    defaultValue={getFailedRequestTraceId() || ''}
                    suffix={
                      <Button
                        appearance="ghost"
                        small
                        leadingIcon={<IconCopy />}
                        onClick={() =>
                          handleCopyCode(getFailedRequestTraceId())
                        }
                        data-testid={`${dataTestId}-copy-code-button`}
                      />
                    }
                    disabled
                    $veneerTheme={theme}
                  />
                </InlineNotificationContainer>
              ) : (
                translate(
                  'components.device-details-viewer.device-card.inline-notifications.errored'
                )
              )
            }
          />
        </InlineErrorCard>
      )}

      <DeviceCardFooter $veneerTheme={theme}>
        {hasCompletedTelemetry && (
          <TelemetryDownloadButton
            data-testid={`${dataTestId}-telemetry-download-button`}
            onDownloadClick={handleDownloadClick}
            telemetryRequestStatus={getDeviceTelemetry.status}
            deviceFamily={device.device_family}
            serialNumber={device.device_serial_nr}
            tabIndex={getDeviceButtonTabIndex()}
          />
        )}
        {shouldShowTelemetryButton && (
          <TelemetryRequestButton
            data-testid={`${dataTestId}-telemetry-request-button`}
            disabled={isDisabled}
            handleOnClick={handleDeviceTelemetry}
            loading={
              postDeviceTelemetry.status === OperationStatus.RUNNING ||
              getDeviceTelemetry.status === OperationStatus.RUNNING
            }
            deviceFamily={device.device_family}
            serialNumber={device.device_serial_nr}
            tabIndex={getDeviceButtonTabIndex()}
          />
        )}
      </DeviceCardFooter>
    </DeviceItemContainer>
  );
};

const DeviceItem = (props: DeviceItemProps): React.ReactElement => (
  <Card
    data-testid={props['data-testid']}
    key={props.device.device_serial_nr}
    background="filled"
    border="outlined"
  >
    <DeviceItemContent {...props} />
  </Card>
);

export default DeviceItem;
